/**
 * AdminReferralConfig — `/admin/referral` — tenant configures the shop
 * referral programme and reviews top referrers.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Share2, Loader2, Save, IndianRupee, Percent, Wallet, Coins, Inbox, ArrowRight } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";

const DEFAULT = {
    enabled: true, mode: "RECURRING",
    one_time_amount: 500, recurring_pct: 2, min_order_value: 99,
    payout_to: "WALLET",
    customer_shop_submission_enabled: true,
};

export default function AdminReferralConfig() {
    const [cfg, setCfg] = useState(DEFAULT);
    const [top, setTop] = useState([]);
    const [subCounts, setSubCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0 });
    const [pendingSubs, setPendingSubs] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [a, b, c] = await Promise.all([
            api.get("/admin/referral-config"),
            api.get("/admin/referrals").catch(() => ({ data: [] })),
            api.get("/admin/shop-submissions").catch(() => ({ data: { items: [], counts: {} } })),
        ]);
        setCfg({ ...DEFAULT, ...a.data });
        setTop(b.data || []);
        setSubCounts(c.data?.counts || { PENDING: 0, APPROVED: 0, REJECTED: 0 });
        setPendingSubs((c.data?.items || []).filter((r) => r.status === "PENDING").slice(0, 5));
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/admin/referral-config", cfg);
            toast.success("Saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-referral">
            <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Share2 size={20} className="text-indigo-500" /> Shop Referral Programme</h1>
            <p className="text-sm text-muted-foreground mt-1">Customers refer shops by sharing their personal code. When a shop is created using their code, the customer earns either a one-time bonus or a % of every order forever.</p>

            {/* Customer submissions — Add Shop pipeline (iter138) */}
            <div className="mt-6 bg-card border border-border rounded-2xl p-5" data-testid="admin-ref-submissions-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-base font-bold inline-flex items-center gap-2">
                            <Inbox size={16} className="text-primary" /> Customer submissions (Add Shop)
                        </h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            Shops that customers have submitted through the <b>Refer</b> page and are awaiting your review.
                        </p>
                        <div className="mt-4 grid grid-cols-3 gap-2 max-w-md">
                            <SubTile label="Pending"  value={subCounts.PENDING  || 0} tone="amber"   testid="sub-count-pending" />
                            <SubTile label="Approved" value={subCounts.APPROVED || 0} tone="emerald" testid="sub-count-approved" />
                            <SubTile label="Rejected" value={subCounts.REJECTED || 0} tone="red"     testid="sub-count-rejected" />
                        </div>
                        {pendingSubs.length > 0 && (
                            <ul className="mt-4 space-y-1.5 text-sm max-w-md">
                                {pendingSubs.map((s) => (
                                    <li key={s.id} className="flex items-center justify-between gap-2 border-b border-border/60 pb-1.5 last:border-0">
                                        <span className="min-w-0 truncate">
                                            <b>{s.shop_name}</b>
                                            <span className="text-muted-foreground"> · {s.city}</span>
                                        </span>
                                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                            {new Date(s.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <Link
                        to="/admin/shop-submissions"
                        data-testid="admin-ref-review-submissions"
                        className="inline-flex items-center gap-1 rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-bold shrink-0"
                    >
                        Review submissions <ArrowRight size={14} />
                    </Link>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="md:col-span-2 bg-card border border-border rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="text-base font-bold">Programme settings</h2>
                        <label className="inline-flex items-center gap-2 text-sm font-semibold">
                            <input data-testid="ref-enabled" type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="accent-primary" />
                            Enabled
                        </label>
                    </div>

                    <fieldset disabled={!cfg.enabled} className="space-y-5 disabled:opacity-50">
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Earning mode</p>
                            <div className="grid grid-cols-2 gap-2">
                                <ModeBtn id="RECURRING" cur={cfg.mode} on={() => setCfg({ ...cfg, mode: "RECURRING" })}
                                    icon={Percent} title="Recurring" sub="% of every order, forever" />
                                <ModeBtn id="ONE_TIME" cur={cfg.mode} on={() => setCfg({ ...cfg, mode: "ONE_TIME" })}
                                    icon={IndianRupee} title="One-time" sub="Single bonus on first order" />
                            </div>
                        </div>

                        {cfg.mode === "RECURRING" ? (
                            <Field label="Recurring %" hint={`Customer earns ${cfg.recurring_pct || 0}% on every order subtotal at every referred shop, indefinitely.`}>
                                <input data-testid="ref-recurring-pct" type="number" step="0.1" value={cfg.recurring_pct} onChange={(e) => setCfg({ ...cfg, recurring_pct: e.target.value })} className="inp" />
                            </Field>
                        ) : (
                            <Field label="One-time amount (₹)" hint={`Customer is credited ${inr(cfg.one_time_amount || 0)} once when a referred shop fulfils its first order over the minimum below.`}>
                                <input data-testid="ref-one-time" type="number" value={cfg.one_time_amount} onChange={(e) => setCfg({ ...cfg, one_time_amount: e.target.value })} className="inp" />
                            </Field>
                        )}

                        <Field label="Minimum order value (₹)" hint="Orders below this don't count.">
                            <input data-testid="ref-min" type="number" value={cfg.min_order_value} onChange={(e) => setCfg({ ...cfg, min_order_value: e.target.value })} className="inp" />
                        </Field>

                        <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Payout destination</p>
                            <div className="grid grid-cols-2 gap-2">
                                <ModeBtn id="WALLET" cur={cfg.payout_to} on={() => setCfg({ ...cfg, payout_to: "WALLET" })}
                                    icon={Wallet} title="Wallet" sub="Customer's in-app wallet (₹)" />
                                <ModeBtn id="COINS" cur={cfg.payout_to} on={() => setCfg({ ...cfg, payout_to: "COINS" })}
                                    icon={Coins} title="Coins" sub="Reward coins (uses Coins config)" />
                            </div>
                        </div>
                    </fieldset>

                    <button data-testid="ref-save" disabled={busy} onClick={save} className="mt-6 bg-primary text-primary-foreground rounded-xl px-5 py-2.5 font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                    </button>
                </div>

                <aside className="bg-card border border-border rounded-2xl p-5 h-fit space-y-5">
                    <div>
                        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Customer "Add Shop"</h3>
                        <label className="mt-3 flex items-start gap-3 cursor-pointer">
                            <input
                                data-testid="ref-add-shop-enabled"
                                type="checkbox"
                                checked={cfg.customer_shop_submission_enabled !== false}
                                onChange={(e) => setCfg({ ...cfg, customer_shop_submission_enabled: e.target.checked })}
                                className="accent-primary mt-0.5"
                            />
                            <span className="text-sm">
                                <span className="font-semibold">Let customers submit new shops</span>
                                <span className="block text-[11px] text-muted-foreground mt-0.5">
                                    Shows an "Add Shop" card on the customer <code className="bg-muted px-1 rounded">/refer</code> page.
                                    Submissions land in <b>Shop Submissions</b> for you to review.
                                </span>
                            </span>
                        </label>
                    </div>
                    <div>
                        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">How it works</h3>
                        <ol className="mt-3 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                            <li>Customer signs up and gets a unique code (`BIZ-XXXXX`).</li>
                            <li>Shop signup or admin "Add Restaurant" accepts a referral code.</li>
                            <li>Every qualifying order at that shop pays the customer per this config.</li>
                            <li>Customer sees their shops + earnings at <code className="bg-muted px-1 rounded">/refer</code>.</li>
                        </ol>
                    </div>
                </aside>
            </div>

            {/* Top referrers */}
            <section className="mt-10">
                <h2 className="text-base font-bold mb-3">Top referrers</h2>
                <div className="bg-card border border-border rounded-2xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                            <tr><th className="px-3 py-2 font-bold">Name</th><th className="px-3 py-2 font-bold">Mobile</th><th className="px-3 py-2 font-bold">Code</th><th className="px-3 py-2 font-bold">Shops</th><th className="px-3 py-2 font-bold">Orders</th><th className="px-3 py-2 font-bold text-right">Earnings</th></tr>
                        </thead>
                        <tbody>
                            {top.length === 0 ? (
                                <tr><td colSpan="6" className="px-3 py-6 text-center text-muted-foreground">No referrals yet.</td></tr>
                            ) : top.map((r) => (
                                <tr key={r.user_id} className="border-t border-border" data-testid={`ref-top-${r.user_id}`}>
                                    <td className="px-3 py-2 font-semibold">{r.name}</td>
                                    <td className="px-3 py-2 text-muted-foreground">{r.mobile}</td>
                                    <td className="px-3 py-2"><code className="text-xs">{r.code}</code></td>
                                    <td className="px-3 py-2">{r.shops}</td>
                                    <td className="px-3 py-2">{r.orders}</td>
                                    <td className="px-3 py-2 text-right font-bold">{inr(r.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
            <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.55rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <div className="mt-1">{children}</div>
            {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
        </label>
    );
}

function ModeBtn({ id, cur, on, icon: Icon, title, sub }) {
    const active = id === cur;
    return (
        <button data-testid={`ref-mode-${id}`} onClick={on} type="button"
            className={`text-left border-2 rounded-xl p-3 transition ${active ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"}`}>
            <Icon size={16} className={active ? "text-primary" : "text-muted-foreground"} />
            <p className="text-sm font-bold mt-1">{title}</p>
            <p className="text-[11px] text-muted-foreground">{sub}</p>
        </button>
    );
}

function SubTile({ label, value, tone, testid }) {
    const toneCls = {
        amber:   "bg-amber-50 border-amber-200 text-amber-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        red:     "bg-red-50 border-red-200 text-red-900",
    }[tone] || "";
    return (
        <div data-testid={testid} className={`rounded-xl border px-3 py-2 ${toneCls}`}>
            <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</p>
            <p className="text-xl font-extrabold leading-none mt-1">{value}</p>
        </div>
    );
}
